import type { PublishedBusinessData } from "../../../types/business";

/** Online quote request is on whenever quotes are enabled. Visit slots stay behind availabilityEnabled. */
export const publicSiteShowsFullQuoteForm = (
  business: PublishedBusinessData,
): boolean => business.quoteFormEnabled === true;

export type ContractorSectionVisibility = {
  services: boolean;
  highlights: boolean;
  projects: boolean;
  testimonials: boolean;
  faqs: boolean;
  serviceAreas: boolean;
  contact: boolean;
};

export type ContractorSectionLink = {
  label: string;
  href: string;
};

type SectionLinkDefinition = ContractorSectionLink & {
  isVisible: (visibility: ContractorSectionVisibility) => boolean;
};

const SECTION_LINK_DEFINITIONS: SectionLinkDefinition[] = [
  {
    label: "Services",
    href: "#services",
    isVisible: ({ services }) => services,
  },
  { label: "About", href: "#about", isVisible: ({ highlights }) => highlights },
  {
    label: "Projects",
    href: "#projects",
    isVisible: ({ projects }) => projects,
  },
  {
    label: "Testimonials",
    href: "#testimonials",
    isVisible: ({ testimonials }) => testimonials,
  },
  {
    label: "Service Area",
    href: "#service-area",
    isVisible: ({ serviceAreas }) => serviceAreas,
  },
  { label: "Contact", href: "#contact", isVisible: ({ contact }) => contact },
];

export const getContractorSectionVisibility = (
  business: PublishedBusinessData,
  aboutImageCount = 0,
): ContractorSectionVisibility => ({
  services: business.services.length > 0,
  highlights:
    business.highlights.length > 0 ||
    (business.aboutBlurb?.trim().length ?? 0) > 0 ||
    aboutImageCount > 0,
  projects: business.projects.length > 0,
  testimonials: business.testimonials.length > 0,
  faqs: business.faqs.length > 0,
  serviceAreas: business.serviceAreas.length > 0,
  contact: true,
});

export const getVisibleContractorSectionLinks = (
  visibility: ContractorSectionVisibility,
): ContractorSectionLink[] =>
  SECTION_LINK_DEFINITIONS.filter((sectionLink) =>
    sectionLink.isVisible(visibility),
  ).map(({ label, href }) => ({ label, href }));

/** First section anchor below the hero, aligned with `ContractorSitePage` render order. */
export const resolveHeroScrollHintHref = (
  visibility: ContractorSectionVisibility,
  showProjects: boolean,
): string => {
  if (visibility.services) return "#services";
  if (visibility.highlights) return "#about";
  if (showProjects) return "#projects";
  if (visibility.testimonials) return "#testimonials";
  if (visibility.serviceAreas) return "#service-area";
  return "#contact";
};
